import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormHelperText,
  Collapse,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  Switch,
  FormControlLabel,
  Grid,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { USERS_CREATE_ENDPOINT, USERS_EDIT_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import { ColorsConstants } from "../../cssUtils/constants";
import ButtonOne from "../../common/normal-button";
import { getCountryCallingCode } from "react-phone-number-input/input";
import CountryCodeSelect from "../../common/country-code-select";
import { getCountryForCallingCode } from "../../common/country-codes";
import { getPhoneValidationError } from "../../common/phone-validation";
import { useTenantCountryDefault } from "../../hooks/useTenantCountry";

let ROLES = ["admin", "manager", "user"];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: 13,
    backgroundColor: "transparent",
    "& fieldset": { borderColor: "#DCDFE5" },
    "&:hover fieldset": { borderColor: "#DCDFE5" },
    "&.Mui-focused fieldset": {
      borderColor: ColorsConstants.primaryBackgroundColor,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: ColorsConstants.primaryBackgroundColor,
  },
  "& .MuiInputLabel-root": {
    fontSize: 13,
    color: "#6C7C93",
  },
  "& .MuiSelect-select": {
    backgroundColor: "transparent",
  },
};

// Helper: get numeric dial code from ISO code
const getDialCode = (isoCode) => {
  try {
    return getCountryCallingCode(isoCode);
  } catch {
    return "";
  }
};

const UserFormPage = (props) => {
  const { getValidToken } = useAuth();
  const [alertObj, setAlertObj] = useState({
    active: false,
    msg: "",
    type: "",
  });

  const mode = props.pageParams?.mode || "add";
  const userId = props.pageParams?.userId;
  const userData = props.pageParams?.userData;

  const initialFormState = {
    name: "",
    email: "",
    country_code: "",
    role: "admin",
    dial_code: "",
    is_active: true,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  // New records start on the tenant's country (Tenant Settings)
  useTenantCountryDefault(
    formData.country_code,
    (iso) => setFormData((prev) => ({ ...prev, country_code: iso })),
    { format: "iso", enabled: mode !== "edit" },
  );

  const activePlan = localStorage.getItem("plan") || "whatsapp_lite";

  if (
    [
      "whatsapp_lite",
      "whatsapp_advanced",
      "whatsapp_advanced_custom_solution",
    ].includes(activePlan)
  ) {
    ROLES = ["admin", "manager", "user"];
  }

  useEffect(() => {
    if (mode === "edit" && userData) {
      let isoCode = userData.country_code || "IN";
      if (/^\d+$/.test(isoCode)) {
        isoCode = getCountryForCallingCode(isoCode) || "IN";
      }
      setFormData({
        name: userData.name || "",
        email: userData.email || "",
        country_code: isoCode,
        role: userData.role || "admin",
        dial_code: userData.dial_code || "",
        is_active: userData.is_active ?? true,
      });
    } else {
      setFormData(initialFormState);
    }
    setFormErrors({});
  }, [mode, userData]);

  const handleClose = () => {
    if (props.onNavigate) props.onNavigate("section_direct_users");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "email" ? value?.toLowerCase() : value,
    }));
  };

  const handleToggleChange = () => {
    setFormData((prev) => ({ ...prev, is_active: !prev.is_active }));
  };

  // Same per-country rule as the other phone fields
  const validatePhoneField = (dialCode, countryCode) =>
    getPhoneValidationError(dialCode, countryCode, getDialCode(countryCode)) || "";
  const validateForm = () => {
    const newErrors = {};
    let hasErrors = false;
    const emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

    if (!formData.name.trim()) {
      newErrors.name = "Please enter a name";
      hasErrors = true;
    }
    if (!formData.email.trim() || !emailPattern.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email";
      hasErrors = true;
    }
    if (!formData.role) {
      newErrors.role = "Please select a role";
      hasErrors = true;
    }

    const phoneError = validatePhoneField(
      formData.dial_code,
      formData.country_code,
    );
    if (phoneError) {
      newErrors.dial_code = phoneError;
      hasErrors = true;
    }

    setFormErrors(newErrors);
    return !hasErrors;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !mode) return;

    const apiUrl =
      mode === "edit"
        ? `${USERS_EDIT_ENDPOINT}${userId}/`
        : USERS_CREATE_ENDPOINT;

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const payload = {
      ...formData,
      country_code: getDialCode(formData.country_code),
    };

    axios({
      method: mode === "edit" ? "patch" : "post",
      url: apiUrl,
      data: payload,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-ID": tenant,
      },
    })
      .then(() => {
        setAlertObj({
          active: true,
          type: "success",
          msg: `User ${mode === "edit" ? "updated" : "created"} successfully`,
        });
        setTimeout(() => {
          if (props.onNavigate) props.onNavigate("section_direct_users");
        }, 1500);
      })
      .catch((error) => {
        const errorMessage = error.response?.data || {};
        const errorMessages = Object.values(errorMessage).join(" ");
        setAlertObj({
          active: true,
          type: "error",
          msg: errorMessages || "Operation failed",
        });
      });
  };

  useEffect(() => {
    let timeoutId;
    if (alertObj.active) {
      timeoutId = setTimeout(
        () => setAlertObj({ active: false, type: "", msg: "" }),
        5000,
      );
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [alertObj]);

  const pageTitle =
    mode === "edit" && userData
      ? `${userData.name || "User"} (${
          userData.role
            ? userData.role.charAt(0).toUpperCase() + userData.role.slice(1)
            : "User"
        })`
      : "Create User";

  const phoneDisplayValue = formData.dial_code ? `${formData.dial_code}` : "";

  return (
    <Box sx={{ backgroundColor: "#FAFAFA", minHeight: "100vh" }}>
      {/* ── Top Header ── */}
      <Box
        sx={{
          backgroundColor: "#fff",
          borderBottom: "1px solid #E5E7EB",
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          onClick={handleClose}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            color: "#04142F",
            "&:hover": { color: ColorsConstants.primaryBackgroundColor },
            mr: 0.5,
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#04142F" }}>
          {pageTitle}
        </Typography>
      </Box>

      {/* ── Form Body ── */}
      <Box sx={{ px: 3, py: 4, width: "100%" }}>
        <Collapse in={alertObj.active}>
          <Alert
            severity={alertObj.type}
            onClose={() => setAlertObj({ active: false, type: "", msg: "" })}
            sx={{ mb: 3, borderRadius: "8px" }}
          >
            {alertObj.msg}
          </Alert>
        </Collapse>

        {/* Row 1: Name + Email */}
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Name *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!formErrors.name}
              helperText={formErrors.name}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              disabled={mode === "edit"}
              sx={fieldSx}
            />
          </Grid>
        </Grid>

        {/* Row 2: Role + Country Code + Phone */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!formErrors.role} sx={fieldSx}>
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleChange}
                label="Role"
                sx={{
                  borderRadius: "8px",
                  fontSize: 13,
                  backgroundColor: "transparent",
                }}
              >
                {ROLES.map((role) => (
                  <MenuItem key={role} value={role} sx={{ fontSize: 13 }}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.role && (
                <FormHelperText>{formErrors.role}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container spacing={2.5}>
              {/* Country Code dropdown */}
              <Grid item xs={12} sm={5}>
                <CountryCodeSelect
                  value={formData.country_code}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, country_code: value }));
                    // Re-validate phone against the new country
                    if (formData.dial_code) {
                      const error = validatePhoneField(
                        formData.dial_code,
                        value,
                      );
                      setFormErrors((prev) => ({
                        ...prev,
                        dial_code: error,
                      }));
                    }
                  }}
                  TextFieldProps={{
                    sx: fieldSx,
                    error: !!formErrors.country_code,
                    helperText: formErrors.country_code,
                  }}
                />
              </Grid>

              {/* Phone Number */}
              <Grid item xs={12} sm={7}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="dial_code"
                  value={phoneDisplayValue}
                  onChange={(e) => {
                    const raw = e.target.value
                      .replace(/^\+\d+-?/, "")
                      .replace(/\D/g, "");
                    setFormData((prev) => ({ ...prev, dial_code: raw }));
                    // Clear or update error live as the user types
                    const error = validatePhoneField(
                      raw,
                      formData.country_code,
                    );
                    setFormErrors((prev) => ({ ...prev, dial_code: error }));
                  }}
                  error={!!formErrors.dial_code}
                  helperText={formErrors.dial_code}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Active Toggle */}
        <Box sx={{ mb: 3.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={handleToggleChange}
                size="small"
                sx={{
                  width: 36,
                  height: 20,
                  padding: 0,
                  "& .MuiSwitch-switchBase": {
                    padding: 0,
                    margin: "2px",
                    "&.Mui-checked": {
                      transform: "translateX(16px)",
                      color: "#fff",
                      "& + .MuiSwitch-track": {
                        backgroundColor: ColorsConstants.primaryBackgroundColor,
                        opacity: 1,
                      },
                    },
                  },
                  "& .MuiSwitch-thumb": {
                    width: 16,
                    height: 16,
                    boxShadow: "none",
                  },
                  "& .MuiSwitch-track": {
                    borderRadius: 10,
                    backgroundColor: "#C8D0E0",
                    opacity: 1,
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: 13, color: "#04142F", ml: 0.5 }}>
                {formData.is_active ? "Active" : "Inactive"}
              </Typography>
            }
            sx={{ margin: 0 }}
          />
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <ButtonOne
            variant="outlined"
            onClick={handleClose}
            sx={{
              height: 40,
              px: 3,
              fontSize: 13,
              border: "1px solid #DCDFE5",
              color: "#04142F",
              backgroundColor: "#fff",
              "&:hover": {
                backgroundColor: "#F5F7FA",
                border: "1px solid #DCDFE5",
              },
            }}
          >
            Cancel
          </ButtonOne>
          <ButtonOne
            variant="contained"
            onClick={handleSubmit}
            sx={{
              height: 40,
              px: 3,
              fontSize: 13,
              backgroundColor: ColorsConstants.primaryBlue100,
              "&:hover": {
                backgroundColor: ColorsConstants.primaryBlue100,
                opacity: 0.9,
              },
            }}
          >
            {mode === "edit" ? "Save Changes" : "Add User"}
          </ButtonOne>
        </Box>
      </Box>
    </Box>
  );
};

export default UserFormPage;
